/** Tiny fetch wrapper. Uses the same-origin /api/* path; works with Vite's proxy in dev. */

const TOKEN_KEY = "ms_auth_token";
const ADMIN_BACKUP_KEY = "ms_auth_token_admin";

export function getAuthToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
export function setAuthToken(token: string | null) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

/** During admin impersonation the admin's original token lives here so "Exit impersonation" can restore it. */
export function getAdminBackupToken(): string | null {
  return localStorage.getItem(ADMIN_BACKUP_KEY);
}
export function setAdminBackupToken(token: string | null) {
  if (token) localStorage.setItem(ADMIN_BACKUP_KEY, token);
  else localStorage.removeItem(ADMIN_BACKUP_KEY);
}

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message?: string) {
    super(message ?? `HTTP ${status}`);
    this.status = status;
    this.body = body;
  }
}

/** Pull a user-facing message off a thrown error; fall back to `fallback`. */
export function getApiErrorMessage(ex: unknown, fallback: string): string {
  return ex instanceof ApiError ? ex.message : fallback;
}

interface RequestOptions extends Omit<RequestInit, "body"> {
  json?: unknown;
}

export async function api<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers = new Headers(opts.headers);
  if (opts.json !== undefined && !headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }
  const token = getAuthToken();
  if (token && !headers.has("authorization")) {
    headers.set("authorization", `Bearer ${token}`);
  }

  const res = await fetch(path, {
    ...opts,
    headers,
    body: opts.json !== undefined ? JSON.stringify(opts.json) : (opts as RequestInit).body,
    credentials: "same-origin",
  });

  const text = await res.text();
  let body: unknown = text;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    /* keep as text */
  }

  if (!res.ok) {
    const message = (body as { message?: string; error?: string })?.message
      ?? (body as { error?: string })?.error
      ?? `HTTP ${res.status}`;
    throw new ApiError(res.status, body, message);
  }
  return body as T;
}
